import { useState } from "react";
import { Button } from "../../components/Button";
import { ISSUED, ISSUED_LABEL, issuedCount } from "./demo";
import { IssuedChip } from "./EinvoiceStatus";
import type { IssuedDoc, IssuedFilter } from "./types";
import "../clients/tables.css";

const FILTERS: { id: IssuedFilter; label: string }[] = [
  { id: "toutes", label: "Toutes" },
  { id: "en_cours", label: "En cours" },
  { id: "acceptee", label: "Acceptées" },
  { id: "refusee", label: "Refusées" },
  { id: "rejet", label: "Rejet technique" },
];

function IssuedActions({ doc, onMock }: { doc: IssuedDoc; onMock: (msg: string) => void }) {
  if (doc.status === "rejet") {
    return (
      <>
        <p className="fx-einv-hint">Le fichier n’est jamais parti. On relance le même PDF, même numéro.</p>
        <div className="fx-einv-acts">
          <Button onClick={() => onMock("Relance fictive — aucun dépôt réel.")}>Relancer le dépôt</Button>
        </div>
      </>
    );
  }
  if (doc.status === "refusee") {
    return (
      <>
        <p className="fx-einv-hint">La facture reste figée. On crée un avoir, puis une nouvelle pièce si besoin.</p>
        <div className="fx-einv-acts">
          <Button onClick={() => onMock("Avoir fictif — l’éditeur de pièces n’est pas encore là.")}>Créer un avoir</Button>
          <Button variant="ghost" onClick={() => onMock("Nouvelle facture : plus tard, dans l’éditeur.")}>
            Nouvelle facture
          </Button>
        </div>
      </>
    );
  }
  if (doc.status === "acceptee") {
    return (
      <>
        <p className="fx-einv-hint">Cycle terminé. Preuve seulement — pas de modification.</p>
        <div className="fx-einv-acts">
          <Button variant="ghost" onClick={() => onMock("Preuve fictive — B2BRouteur n’est pas branché.")}>
            Télécharger la preuve
          </Button>
        </div>
      </>
    );
  }
  return <p className="fx-einv-hint">On attend le destinataire. Pas de bouton Modifier.</p>;
}

export function EinvoiceIssued() {
  const [filter, setFilter] = useState<IssuedFilter>("toutes");
  const [openId, setOpenId] = useState<string | null>(null);
  const [flash, setFlash] = useState("");
  const rows = ISSUED.filter((row) => {
    if (filter === "toutes") return true;
    if (filter === "en_cours") return row.status === "en_cours" || row.status === "deposee";
    return row.status === filter;
  });
  const doc = ISSUED.find((row) => row.id === openId) ?? null;
  const viewKey = doc ? `issued-${doc.id}` : `list-${filter}`;

  return (
    <div className="fx-page" key={viewKey}>
      {doc ? (
        <article className="fx-fiche">
          <div className="fx-fiche-head">
            <div className="fx-fiche-head__id">
              <button type="button" className="fx-fiche-back" onClick={() => { setOpenId(null); setFlash(""); }}>
                ← Liste
              </button>
              <h1>
                {doc.number}
                <span className="fx-fiche-head__badge">{doc.client}</span>
              </h1>
            </div>
            <IssuedChip status={doc.status} />
          </div>
          {flash ? <p className="fx-einv-flash">{flash}</p> : null}
          <div className="fx-fiche-cards">
            <section className="fx-fiche-card">
              <h2>Parcours</h2>
              <ol className="fx-einv-steps">
                {doc.steps.map((step) => (
                  <li key={step.label} className={step.done ? "" : "is-wait"}>
                    <b>{step.label}</b>
                    <span>{step.at}</span>
                  </li>
                ))}
              </ol>
            </section>
            <section className="fx-fiche-card">
              <h2>Motif · {doc.amount}</h2>
              <p className="fx-einv-hint">{doc.motif}</p>
              <IssuedActions doc={doc} onMock={setFlash} />
            </section>
          </div>
        </article>
      ) : (
        <>
          <div className="fx-einv-kpis">
            <dl className="fx-einv-kpi">
              <dt>En cours</dt>
              <dd>{issuedCount("en_cours") + issuedCount("deposee")}</dd>
            </dl>
            <dl className="fx-einv-kpi">
              <dt>Acceptées</dt>
              <dd>{issuedCount("acceptee")}</dd>
            </dl>
            <dl className="fx-einv-kpi">
              <dt>Refusées</dt>
              <dd>{issuedCount("refusee")}</dd>
            </dl>
            <dl className="fx-einv-kpi">
              <dt>Rejet</dt>
              <dd>{issuedCount("rejet")}</dd>
            </dl>
          </div>
          <div className="fx-einv-filters" role="group" aria-label="Filtrer">
            {FILTERS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={filter === item.id ? "is-on" : ""}
                onClick={() => setFilter(item.id)}
              >
                {item.id === "toutes" ? item.label : ISSUED_LABEL[item.id]}
              </button>
            ))}
          </div>
          <div className="fx-clients-wrap">
            <table className="fx-clients-table">
              <thead>
                <tr>
                  <th scope="col">N°</th>
                  <th scope="col">Client</th>
                  <th className="is-num" scope="col">Montant</th>
                  <th scope="col">Déposée le</th>
                  <th scope="col">Statut</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="fx-clients-table__row" onClick={() => setOpenId(row.id)}>
                    <td>
                      <span className="fx-clients-table__name">{row.number}</span>
                    </td>
                    <td>{row.client}</td>
                    <td className="is-num">{row.amount}</td>
                    <td className="fx-clients-table__muted">{row.sentAt}</td>
                    <td>
                      <IssuedChip status={row.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
